import { Sentry } from '$lib/services/sentry';

/**
 * Get query params.
 * @param {URL} url url
 * @param {string} paramKey param key
 * @returns {string | null} query param value
 */
export function getQueryParamByKey(url, paramKey) {
	try {
		return url.searchParams.get(paramKey) || null;
	} catch (error) {
		Sentry.captureException(error, {
			extra: {
				action: `Error: Parsing URL Params ${url}`
			}
		});
	}
	return null;
}

/**
 * Parse an Error into an object containing the stack trace and structured stack frame objects.
 * Always returns exactly minDepth stack frames (or all available if less than minDepth).
 * @param {unknown} error            - The error to parse (Error or anything).
 * @param {number} [minDepth]        - Minimum number of frames to return. Defaults to 6.
 * @returns {{
 *   stack: string | null,
 *   frames: Array<{
 *     name: string | null,
 *     message: string | null,
 *     functionName: string | null,
 *     file: string | null,
 *     line: number | null,
 *     column: number | null,
 *     rawFrame: string | null
 *   }>
 * }}
 */
export function parseError(error, minDepth = 6) {
	// Default frame structure for error cases
	const defaultFrame = {
		name: null,
		message: null,
		functionName: null,
		file: null,
		line: null,
		column: null,
		rawFrame: null
	};

	// Default return value - object with stack and frames
	const defaultReturn = {
		stack: null,
		frames: Array(minDepth)
			.fill(null)
			.map(() => ({ ...defaultFrame }))
	};

	try {
		const err =
			error instanceof Error
				? error
				: new Error(typeof error === 'string' ? error : 'Non-Error thrown');

		const stack = typeof err.stack === 'string' ? err.stack : null;
		const name = typeof err.name === 'string' ? err.name : null;
		const message = typeof err.message === 'string' ? err.message : null;

		const lines = stack ? stack.split('\n') : [];

		// Common V8-style / browser-style stack:
		// 0: "Error: message"
		// 1: "    at functionName (file:line:col)"
		// 2: "    at caller (file:line:col)"
		// 3: "    at anotherCaller (file:line:col)"
		// ...
		const frames = [];

		// Tolerant regex patterns:
		//   at funcName (path/to/file.js:10:5)
		//   at path/to/file.js:10:5
		//   at funcName (<anonymous>:1:1)
		//   at async http://localhost:5173/file.js:10:5
		// Handles URLs with colons, query parameters, and async keyword
		// First match the line:column pattern at the end, then extract file and function name
		const lineColRegex = /:(\d+):(\d+)\)?$/;
		const funcNameRegex = /^at\s+(?:async\s+)?([^\s(]+)\s+\(/;

		// Parse all available stack frames
		for (let i = 1; i < lines.length; i++) {
			const rawFrame = lines[i]?.trim() ?? null;

			if (!rawFrame) {
				continue;
			}

			// Extract line and column from the end
			const lineColMatch = rawFrame.match(lineColRegex);
			if (!lineColMatch) {
				continue;
			}

			const line = Number(lineColMatch[1]);
			const column = Number(lineColMatch[2]);

			// Remove the line:column part to get the rest
			const beforeLineCol = rawFrame.substring(0, lineColMatch.index);

			// Extract function name if present
			let functionName = null;
			const funcMatch = beforeLineCol.match(funcNameRegex);
			if (funcMatch) {
				functionName = funcMatch[1];
			}

			// Extract file name - it's everything after "at " or "at async " or "at functionName ("
			// Remove "at " prefix
			let filePart = beforeLineCol.replace(/^at\s+(?:async\s+)?/, '');
			// Remove function name and opening parenthesis if present
			if (funcMatch) {
				filePart = filePart.replace(/^[^\s(]+\s+\(/, '').replace(/^\(/, '');
			}
			// Remove closing parenthesis if present (shouldn't be, but just in case)
			filePart = filePart.replace(/\)$/, '').trim();

			frames.push({
				name,
				message,
				functionName,
				file: filePart || null,
				line,
				column,
				rawFrame
			});
		}

		// Limit to minDepth frames if we have more than minDepth
		if (frames.length > minDepth) {
			frames.length = minDepth;
		}

		// Pad to minDepth if we have fewer frames
		while (frames.length < minDepth) {
			frames.push({
				name,
				message,
				functionName: null,
				file: null,
				line: null,
				column: null,
				rawFrame: null
			});
		}

		return {
			stack,
			frames
		};
	} catch (err) {
		// Log the error but don't throw - return default frames instead
		console.error('Error parsing stack trace:', err);
		if (typeof Sentry !== 'undefined' && Sentry.captureException) {
			Sentry.captureException(err, {
				extra: {
					action: 'parseError function failure',
					originalError: error
				}
			});
		}
		return defaultReturn;
	}
}
